(() => {
  const KEYS = {
    vehicle: "dorimm_vehicle_profile_v1",
    localVehicle: "dorimm_selected_vehicle_v2",
    saved: "dorimm_saved_wheels_v1",
    compare: "dorimm_compare_wheels_v1",
  };
  const read = (key, fallback) => {
    try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; }
    catch (_error) { return fallback; }
  };
  const write = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); }
    catch (_error) { /* Shopping remains usable when storage is unavailable. */ }
  };
  const escapeHtml = (value) => String(value ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
  const message = (key, fallback) => window.DORIMM_I18N?.[key] || fallback;
  const formatMessage = (key, fallback, values = {}) => Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll(`{{ ${name} }}`, String(value)),
    message(key, fallback),
  );
  const localizedPath = (path) => {
    const root = String(window.DORIMM_I18N?.root || "/").replace(/\/$/, "");
    return `${root}${path}` || path;
  };
  const unique = (items) => [...new Map(items.map((item) => [item.handle, item])).values()];
  const profileFromLocalFitment = () => {
    const saved = read(KEYS.localVehicle, null);
    const fitmentData = window.DORIMM_FITMENT_DATA;
    const vehicle = fitmentData?.vehicles?.find((item) => item.id === saved?.id);
    if (!vehicle) return null;
    const matches = Object.values(fitmentData.skus || {}).flatMap((sku) =>
      (sku.fitments || [])
        .filter((fitment) => fitment.vehicleId === vehicle.id)
        .map((fitment) => ({
          ...fitment,
          sku: sku.sku,
          productHandle: sku.handle,
          diameter: sku.diameter,
          width: sku.width,
          pcd: sku.pcd,
          offset: sku.et,
          centerBore: sku.cb,
          finish: sku.finish,
          status: "candidate",
        })),
    );
    return {
      source: "dorimm-local-fitment-v2",
      selection: {
        year: saved.year || vehicle.yearFrom,
        make: vehicle.make,
        model: vehicle.model,
        trim: vehicle.trim,
      },
      vehicle: {
        ...vehicle,
        boltCount: vehicle.lugCount,
        centerBore: vehicle.hubBore,
      },
      matches,
      savedAt: saved.savedAt || Date.now(),
    };
  };
  const removeStoredVehicle = () => {
    try {
      localStorage.removeItem(KEYS.vehicle);
      localStorage.removeItem(KEYS.localVehicle);
    } catch (_error) {
      /* Invalid or unavailable storage must fail closed, never restore a stale vehicle. */
    }
  };
  const validWorkerProfile = (profile) => {
    if (!profile || typeof profile !== "object") return false;
    const selection = profile.selection || {};
    const vehicle = profile.vehicle || {};
    return Boolean(
      String(selection.year || vehicle.year || "").trim()
      && String(selection.make || vehicle.make || "").trim()
      && String(selection.model || vehicle.model || "").trim()
    );
  };
  const workerProfileBootstrap = (() => {
    let raw = null;
    try { raw = localStorage.getItem(KEYS.vehicle); }
    catch (_error) { return { present: true, value: null }; }
    if (raw === null) return { present: false, value: null };
    let value = null;
    try { value = JSON.parse(raw); }
    catch (_error) { /* Invalid JSON is handled below. */ }
    if (!validWorkerProfile(value)) {
      removeStoredVehicle();
      return { present: true, value: null };
    }
    return { present: true, value };
  })();
  const state = {
    vehicle: workerProfileBootstrap.present ? workerProfileBootstrap.value : profileFromLocalFitment(),
    saved: read(KEYS.saved, []),
    compare: read(KEYS.compare, []),
  };
  if (!workerProfileBootstrap.present && state.vehicle) write(KEYS.vehicle, state.vehicle);

  const productFrom = (button) => {
    const root = button.closest("[data-product-context]");
    if (!root?.dataset.productHandle) return null;
    return {
      handle: root.dataset.productHandle,
      title: root.dataset.productTitle || root.dataset.productHandle,
      url: root.dataset.productUrl || `/products/${root.dataset.productHandle}`,
      price: root.dataset.productPrice || "",
      series: root.dataset.productSeries || message("catalogLabel", "Dorimm catalog"),
      image: root.dataset.productImage || "",
    };
  };
  const vehicleLabel = (profile) => {
    const selection = profile?.selection || {};
    const labels = profile?.labels || {};
    const vehicle = profile?.vehicle || {};
    const readable = (value) => {
      const text = String(value || "").trim();
      if (!text || /^[a-f\d-]{8,}$/i.test(text)) return "";
      return text.replaceAll("-", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    };
    const year = readable(labels.year || selection.year || vehicle.year || vehicle.yearFrom);
    const make = readable(labels.make || selection.make || vehicle.make);
    const model = readable(labels.model || selection.model || vehicle.model);
    const trim = readable(labels.modification || labels.trim || labels.generation || selection.trim || selection.modification || selection.generation || vehicle.trim || vehicle.generation);
    return [year, make, model, trim].filter(Boolean).join(" ") || message("selectedVehicleLabel", "Selected vehicle");
  };
  const vehicleSpecs = (profile) => {
    const vehicle = profile?.vehicle || {};
    const pcd = vehicle.pcd || (vehicle.boltCircle ? `${vehicle.lugCount || vehicle.boltCount}×${vehicle.boltCircle}` : "");
    return [pcd, vehicle.centerBore || vehicle.hubBore ? `CB ${vehicle.centerBore || vehicle.hubBore} mm` : ""].filter(Boolean).join(" · ");
  };
  const toast = (message) => {
    const node = document.querySelector("[data-commerce-toast]");
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
    node.classList.add("is-visible");
    window.clearTimeout(toast.timer);
    toast.timer = window.setTimeout(() => { node.classList.remove("is-visible"); window.setTimeout(() => { node.hidden = true; }, 240); }, 2200);
  };
  const renderGarage = () => {
    const garage = document.querySelector("[data-dorimm-garage]");
    if (garage) {
      const revealGarage = garage.hidden && Boolean(state.vehicle);
      garage.hidden = !state.vehicle;
      if (state.vehicle) {
        garage.querySelector("[data-garage-label]").textContent = vehicleLabel(state.vehicle);
        garage.querySelector("[data-garage-specs]").textContent = vehicleSpecs(state.vehicle) || `${state.vehicle.matches?.length || 0} ${message("matchedConfigurations", "matched configurations")}`;
        if (revealGarage && window.DorimmMotion?.enabled && window.gsap) {
          window.gsap.fromTo(garage, { autoAlpha: 0, y: -8, scale: 0.97 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, ease: "power3.out", clearProps: "transform,opacity,visibility" });
        }
      }
    }
    document.querySelectorAll("[data-hero-personalization]").forEach((hero) => {
      const hook = hero.querySelector("[data-hero-vehicle-hook]");
      const label = hero.querySelector("[data-hero-vehicle-label]");
      if (!hook || !label) return;
      hook.hidden = !state.vehicle;
      if (state.vehicle) label.textContent = vehicleLabel(state.vehicle);
    });
  };
  const renderButtons = () => {
    document.querySelectorAll("[data-save-product]").forEach((button) => {
      const product = productFrom(button);
      const active = Boolean(product && state.saved.some((item) => item.handle === product.handle));
      button.setAttribute("aria-pressed", String(active));
      button.classList.toggle("is-active", active);
      button.lastChild.textContent = ` ${active ? message("savedLabel", "SAVED") : message("saveLabel", "SAVE")}`;
    });
    document.querySelectorAll("[data-compare-product]").forEach((button) => {
      const product = productFrom(button);
      const active = Boolean(product && state.compare.some((item) => item.handle === product.handle));
      button.setAttribute("aria-pressed", String(active));
      button.classList.toggle("is-active", active);
    });
  };
  const renderCompare = () => {
    const list = document.querySelector("[data-compare-items]");
    if (!list) return;
    list.innerHTML = state.compare.length ? state.compare.map((item, index) => `<article data-compare-handle="${escapeHtml(item.handle)}"><span>${String(index + 1).padStart(2, "0")}</span>${item.image ? `<img src="${escapeHtml(item.image)}" alt="" width="180" height="180">` : ""}<div><small>${escapeHtml(item.series)}</small><h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a></h3><b>${escapeHtml(item.price)}</b></div><button type="button" data-compare-remove="${escapeHtml(item.handle)}" aria-label="${escapeHtml(formatMessage("removeCompare", "Remove {{ title }} from comparison", { title: item.title }))}">×</button></article>`).join("") : `<div class="dorimm-compare__empty"><small>${escapeHtml(message("compareEmptyTitle", "NO WHEELS SELECTED"))}</small><p>${escapeHtml(message("compareEmptyHelp", "Use Compare on any wheel card to build a focused shortlist."))}</p></div>`;
  };
  const openCompare = () => {
    const drawer = document.querySelector("[data-compare-drawer]");
    if (!drawer) return;
    renderCompare();
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add("is-open"));
    document.body.classList.add("compare-open");
  };
  const closeCompare = () => {
    const drawer = document.querySelector("[data-compare-drawer]");
    if (!drawer) return;
    drawer.classList.remove("is-open");
    document.body.classList.remove("compare-open");
    window.setTimeout(() => { drawer.hidden = true; }, 280);
  };
  const applyVehicleMatches = () => {
    document.querySelectorAll("[data-product-context]").forEach((card) => {
      card.classList.remove("is-garage-match");
      delete card.dataset.fitmentMatch;
      const badge = card.querySelector("[data-card-fitment]");
      if (badge) badge.hidden = true;
      card.querySelectorAll("a[data-fitment-base-href]").forEach((link) => {
        link.setAttribute("href", link.dataset.fitmentBaseHref);
      });
    });
    document.querySelectorAll("[data-home-grid]").forEach((grid) => {
      const cards = [...grid.querySelectorAll("[data-product-context]")];
      cards.forEach((card, index) => {
        if (!card.dataset.fitmentBaseOrder) card.dataset.fitmentBaseOrder = String(index + 1);
      });
      cards
        .sort((left, right) => Number(left.dataset.fitmentBaseOrder) - Number(right.dataset.fitmentBaseOrder))
        .forEach((card) => grid.appendChild(card));
      grid.dataset.personalized = "false";
    });
    document.querySelectorAll("[data-collection-experience]").forEach((collection) => {
      collection.dataset.collectionMatchMode = "all";
      const context = collection.querySelector("[data-collection-fitment]");
      if (context) context.hidden = true;
      collection.dispatchEvent(new CustomEvent("dorimm:collection-context", { detail: { mode: "all" } }));
    });
    if (!state.vehicle) return;
    const matches = Array.isArray(state.vehicle.matches) ? state.vehicle.matches : [];
    const handles = new Set(matches.map((match) => match.productHandle).filter(Boolean));
    document.querySelectorAll("[data-product-context]").forEach((card) => {
      const matched = handles.has(card.dataset.productHandle);
      card.classList.toggle("is-garage-match", matched);
      card.dataset.fitmentMatch = String(matched);
      const badge = card.querySelector("[data-card-fitment]");
      if (badge) {
        const matchCount = matches.filter((match) => match.productHandle === card.dataset.productHandle).length;
        badge.hidden = !matched;
        if (matched) badge.textContent = `${matchCount} ${message(matchCount === 1 ? "dimensionalCandidateLabel" : "dimensionalCandidates", matchCount === 1 ? "dimensional candidate" : "DIMENSIONAL CANDIDATES")}`;
      }
      if (matched) {
        const firstMatch = matches.find((match) => match.productHandle === card.dataset.productHandle);
        card.querySelectorAll("a[href]").forEach((link) => {
          if (!link.dataset.fitmentBaseHref) {
            link.dataset.fitmentBaseHref = link.getAttribute("href") || link.href;
          }
          const url = new URL(link.href, window.location.origin);
          const vehicleId = state.vehicle.vehicle?.id || state.vehicle.vehicleId || state.vehicle.id;
          if (vehicleId) url.searchParams.set("vehicle_id", vehicleId);
          if (state.vehicle.selection?.year) url.searchParams.set("vehicle_year", state.vehicle.selection.year);
          if (firstMatch?.sku) url.searchParams.set("sku", firstMatch.sku);
          link.href = `${url.pathname}${url.search}`;
        });
      }
    });
    document.querySelectorAll("[data-home-grid]").forEach((grid) => {
      const cards = [...grid.querySelectorAll("[data-product-context]")];
      const ordered = [
        ...cards.filter((card) => card.dataset.fitmentMatch === "true"),
        ...cards.filter((card) => card.dataset.fitmentMatch !== "true"),
      ];
      const mutate = () => ordered.forEach((card) => grid.appendChild(card));
      if (window.DorimmMotion?.enabled) window.DorimmMotion.flip(cards, mutate, { duration: 0.52 });
      else mutate();
      grid.dataset.personalized = handles.size ? "true" : "false";
    });
    document.querySelectorAll("[data-collection-experience]").forEach((collection) => {
      const context = collection.querySelector("[data-collection-fitment]");
      const productHandles = new Set([...collection.querySelectorAll("[data-product-card]")].map((card) => card.dataset.productHandle));
      const matchedProducts = [...handles].filter((handle) => productHandles.has(handle)).length;
      collection.dataset.collectionMatchMode = "matches";
      if (!context) return;
      context.innerHTML = `<div><small>${escapeHtml(message("dimensionalCandidates", "DIMENSIONAL CANDIDATES"))}</small><strong>${escapeHtml(vehicleLabel(state.vehicle))}</strong><span>${escapeHtml(vehicleSpecs(state.vehicle))}</span></div><div class="collection-fitment-specs"><span><small>${escapeHtml(message("wheelStyles", "WHEEL STYLES"))}</small><b>${matchedProducts}</b></span><span><small>${escapeHtml(message("configurations", "CONFIGURATIONS"))}</small><b>${matches.length}</b></span></div><p><b>${escapeHtml(message("finalReviewRequired", "FINAL REVIEW REQUIRED"))}</b> ${escapeHtml(message("vehicleRuleHelp", "PCD, diameter, width, offset and center bore passed the current rules."))}</p><div class="collection-fitment-actions"><button class="is-active" type="button" data-fitment-mode="matches">${escapeHtml(message("showMatches", "SHOW MATCHES"))}</button><button type="button" data-fitment-mode="all">${escapeHtml(message("showAll", "SHOW ALL"))}</button><a href="${escapeHtml(localizedPath("/pages/fitment"))}">${escapeHtml(message("changeVehicle", "CHANGE VEHICLE"))}</a></div>`;
      context.hidden = false;
      collection.dispatchEvent(new CustomEvent("dorimm:collection-context", { detail: { mode: "matches", matchedProducts } }));
    });
  };
  const parseProductVariants = (root) => {
    try { return JSON.parse(root.querySelector("[data-product-variants]")?.textContent || "[]"); }
    catch (_error) { return []; }
  };
  const syncPdpGarage = (root) => {
    if (root.dataset.pdpFitmentOwner === "dorimm-fitment") return;
    if (!state.vehicle || !root.matches("[data-product-fitment-root]")) return;
    const matches = (state.vehicle.matches || []).filter((match) => match.productHandle === root.dataset.productHandle);
    const form = root.querySelector(".dorimm-product-form");
    const master = form?.querySelector("[name='id']");
    const panel = root.querySelector("[data-product-fitment]");
    const atc = form?.querySelector("[data-product-atc]");
    const purchasePrompt = form?.querySelector("[data-product-atc-note]");
    const consent = form?.querySelector("[data-fitment-consent]");
    if (!form || !master || !panel || !atc || !consent) return;
    const variants = parseProductVariants(root);
    const matchIds = new Set(matches.map((match) => String(match.variantId).split("/").pop()));
    if (master.tagName === "SELECT") {
      [...master.options].forEach((option) => {
        const matched = matchIds.has(option.value);
        option.hidden = !matched;
        option.disabled = !matched || option.disabled;
      });
      if (!matchIds.has(master.value) && matches.length) master.value = String(matches[0].variantId).split("/").pop();
    }
    const sync = () => {
      const selectedId = String(master.value || "");
      const match = matches.find((item) => String(item.variantId).split("/").pop() === selectedId);
      const variant = variants.find((item) => String(item.id) === selectedId);
      root.querySelectorAll("[data-product-option]").forEach((select, index) => {
        if (variant?.options?.[index]) select.value = variant.options[index];
      });
      const property = (name, value) => {
        const input = form.querySelector(`[name="properties[${name}]"]`);
        if (input) input.value = value || "";
      };
      property("Selected vehicle", vehicleLabel(state.vehicle));
      property("Fitment status", match ? (match.status === "verified" ? message("verifiedMatch", "Verified dimensional match") : message("conditionalMatch", "Conditional dimensional match")) : message("noCurrentMatch", "No current match"));
      property("Wheel SKU", match?.sku || variant?.sku || "");
      const vehicleGeometry = state.vehicle.vehicle || {};
      const boltCount = vehicleGeometry.boltCount || vehicleGeometry.lugCount;
      const boltCircle = vehicleGeometry.pcd || vehicleGeometry.boltCircle;
      const vehiclePcd = boltCount && boltCircle ? `${boltCount}×${boltCircle}` : (boltCircle || "");
      property("Vehicle PCD", vehiclePcd);
      property("Hub ring", match?.hubRing || "");
      const selectedVehicleKey = ["region", "make", "model", "year", "generation", "modification"]
        .map((key) => state.vehicle.selection?.[key])
        .filter(Boolean)
        .join("|");
      property("_Vehicle ID", selectedVehicleKey);
      property("_Fitment rule", "dorimm-fitment-v1");
      const confirmation = consent.closest(".fitment-confirm");
      confirmation.hidden = !match;
      consent.required = Boolean(match);
      atc.disabled = !(match && consent.checked && variant?.available !== false);
      if (purchasePrompt) purchasePrompt.textContent = match ? message("addWithReview", "ADD WITH FITMENT REVIEW") : message("noCandidate", "NO CURRENT CANDIDATE");
      if (!matches.length) {
        panel.innerHTML = `<small>${escapeHtml(message("noCandidate", "NO CURRENT CANDIDATE"))}</small><strong>${escapeHtml(vehicleLabel(state.vehicle))}</strong><p>${escapeHtml(message("noCandidateHelp", "This wheel has no current dimensional candidate for your vehicle."))}</p><a href="${escapeHtml(localizedPath("/collections/all"))}">${escapeHtml(message("viewCandidates", "VIEW CANDIDATE WHEELS"))}</a>`;
      } else if (match) {
        const specs = [`${match.diameter || "—"}×${match.width || "—"}`, match.offset != null ? `ET ${match.offset}` : "", match.boltCount && match.pcd ? `${match.boltCount}×${match.pcd}` : "", match.centerBore ? `CB ${match.centerBore}` : ""].filter(Boolean);
        panel.innerHTML = `<small>${escapeHtml(message("candidate", "DIMENSIONAL CANDIDATE"))} · ${escapeHtml(message("finalReviewRequired", "FINAL REVIEW REQUIRED"))}</small><strong>${escapeHtml(vehicleLabel(state.vehicle))}</strong><div class="product-fitment-facts"><span><small>${escapeHtml(message("wheelSku", "WHEEL SKU"))}</small><b>${escapeHtml(match.sku)}</b></span><span><small>${escapeHtml(message("configuration", "CONFIGURATION"))}</small><b>${escapeHtml(specs.join(" · "))}</b></span><span><small>${escapeHtml(message("hub", "HUB"))}</small><b>${escapeHtml(match.hubRing || message("directReview", "Direct / review"))}</b></span></div><p>${escapeHtml(message("manualHelp", "Brake clearance, hardware and vehicle modifications are reviewed before fulfillment."))}</p><a href="${escapeHtml(localizedPath("/pages/fitment"))}">${escapeHtml(message("changeVehicle", "CHANGE VEHICLE"))}</a>`;
      }
    };
    if (!root.dataset.garagePdpReady) {
      root.dataset.garagePdpReady = "true";
      master.addEventListener("change", () => queueMicrotask(sync));
      root.querySelectorAll("[data-product-option]").forEach((select) => select.addEventListener("change", () => queueMicrotask(sync)));
      consent.addEventListener("change", sync);
    }
    sync();
  };
  const initAjaxCart = (scope) => {
    scope.querySelectorAll?.(".dorimm-product-form").forEach((form) => {
      if (form.dataset.ajaxReady) return;
      form.dataset.ajaxReady = "true";
      const primaryButton = form.querySelector("[data-product-atc]");
      const buyNowButton = form.querySelector("[data-product-buy-now]");
      if (primaryButton && buyNowButton) {
        buyNowButton.addEventListener("click", () => {
          if (
            form.dataset.purchaseReady !== "true"
            || buyNowButton.disabled
            || primaryButton.disabled
            || form.dataset.cartSubmitting === "true"
          ) return;
          if (!form.reportValidity()) return;
          form.dataset.checkoutAfterAdd = "true";
          form.requestSubmit(primaryButton);
        });
      }
      form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (form.dataset.cartSubmitting === "true") return;
        const button = form.querySelector("[data-product-atc]");
        if (!button || button.disabled || form.dataset.purchaseReady !== "true") return;
        if (!form.reportValidity()) return;
        const label = button.querySelector("[data-product-atc-label]");
        if (!label) return;
        const checkoutAfterAdd = form.dataset.checkoutAfterAdd === "true";
        const original = label.textContent;
        form.dataset.cartSubmitting = "true";
        button.disabled = true;
        button.setAttribute("aria-busy", "true");
        if (buyNowButton) {
          buyNowButton.disabled = true;
          buyNowButton.setAttribute("aria-busy", "true");
        }
        label.textContent = `${message("addingConfiguration", "ADDING CONFIGURATION")}…`;
        const releaseSubmitLock = () => {
          delete form.dataset.cartSubmitting;
          delete form.dataset.checkoutAfterAdd;
          button.removeAttribute("aria-busy");
          buyNowButton?.removeAttribute("aria-busy");
          label.textContent = original;
          form.dispatchEvent(new CustomEvent("dorimm:pdp-gate-refresh"));
        };
        try {
          const formData = new FormData(form);
          const properties = {};
          for (const [name, value] of formData.entries()) {
            if (!name.startsWith("properties[") || !name.endsWith("]") || !value) continue;
            const propertyName = name.slice(11, -1);
            if (propertyName) properties[propertyName] = String(value);
          }
          const item = {
            id: Number(formData.get("id")),
            quantity: Math.max(1, Number(formData.get("quantity")) || 1),
            properties,
          };
          const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify({ items: [item] }),
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || message("unableAdd", "Unable to add this configuration"));
          document.dispatchEvent(new CustomEvent("dorimm:analytics", { detail: { event: "add_to_cart", variant_id: item.id, quantity: item.quantity, wheel_sku: properties["Wheel SKU"] || "", vehicle: properties["Selected vehicle"] || "", purchase_route: properties["_Purchase route"] || "" } }));
          if (checkoutAfterAdd) {
            window.location.assign(localizedPath("/checkout"));
            return;
          }
          let cart = null;
          try {
            cart = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, { headers: { Accept: "application/json" } }).then((item) => item.json());
            document.querySelectorAll(".bag-link span").forEach((node) => { node.textContent = cart.item_count; });
          } catch (_cartRefreshError) {
            // The line item was added successfully. A non-critical badge refresh must not invite a duplicate retry.
          }
          document.dispatchEvent(new CustomEvent("dorimm:cart-added", { detail: { cart } }));
          label.textContent = message("addedBag", "ADDED TO BAG ✓");
          toast(message("addedBagHelp", "Matched configuration added to your bag"));
          window.setTimeout(releaseSubmitLock, 1800);
        } catch (error) {
          releaseSubmitLock();
          toast(error.message || message("unableAdd", "Unable to add this configuration"));
        }
      });
    });
  };
  const init = (scope = document) => {
    scope.querySelectorAll?.("[data-save-product]").forEach((button) => {
      if (button.dataset.commerceReady) return;
      button.dataset.commerceReady = "true";
      button.addEventListener("click", () => {
        const product = productFrom(button);
        if (!product) return;
        const exists = state.saved.some((item) => item.handle === product.handle);
        state.saved = exists ? state.saved.filter((item) => item.handle !== product.handle) : unique([...state.saved, product]);
        write(KEYS.saved, state.saved);
        renderButtons();
        toast(exists ? message("removedSaved", "Removed from saved wheels") : message("savedShortlist", "Saved to your shortlist"));
      });
    });
    scope.querySelectorAll?.("[data-compare-product]").forEach((button) => {
      if (button.dataset.commerceReady) return;
      button.dataset.commerceReady = "true";
      button.addEventListener("click", () => {
        const product = productFrom(button);
        if (!product) return;
        const exists = state.compare.some((item) => item.handle === product.handle);
        if (exists) state.compare = state.compare.filter((item) => item.handle !== product.handle);
        else if (state.compare.length < 3) state.compare = unique([...state.compare, product]);
        else { toast(message("compareLimit", "Compare supports up to 3 wheels")); openCompare(); return; }
        write(KEYS.compare, state.compare);
        renderButtons();
        openCompare();
      });
    });
    renderGarage();
    renderButtons();
    applyVehicleMatches();
    scope.querySelectorAll?.("[data-product-fitment-root]").forEach(syncPdpGarage);
    initAjaxCart(scope);
  };

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-compare-close]")) closeCompare();
    const remove = event.target.closest("[data-compare-remove]");
    if (remove) {
      state.compare = state.compare.filter((item) => item.handle !== remove.dataset.compareRemove);
      write(KEYS.compare, state.compare);
      renderCompare(); renderButtons();
    }
    if (event.target.closest("[data-compare-clear]")) {
      state.compare = []; write(KEYS.compare, state.compare); renderCompare(); renderButtons();
    }
    if (event.target.closest("[data-garage-clear]")) {
      state.vehicle = null;
      localStorage.removeItem(KEYS.vehicle);
      localStorage.removeItem(KEYS.localVehicle);
      document.dispatchEvent(new CustomEvent("dorimm:vehicle-cleared"));
      toast(message("vehicleSelectionCleared", "Vehicle selection cleared"));
    }
    const fitmentMode = event.target.closest("button[data-fitment-mode]");
    if (fitmentMode) {
      const collection = fitmentMode.closest("[data-collection-experience]");
      if (collection) {
        const mode = fitmentMode.dataset.fitmentMode;
        collection.querySelectorAll("button[data-fitment-mode]").forEach((button) => button.classList.toggle("is-active", button === fitmentMode));
        collection.dataset.collectionMatchMode = mode;
        collection.dispatchEvent(new CustomEvent("dorimm:collection-context", { detail: { mode } }));
      }
    }
    if (event.target.closest("[data-garage-open]")) window.location.href = localizedPath("/pages/fitment");
  });
  document.addEventListener("keydown", (event) => { if (event.key === "Escape") closeCompare(); });
  document.addEventListener("dorimm:vehicle-selected", (event) => {
    const finder = event.target?.closest?.("[data-dorimm-fitment]") || document.querySelector("[data-dorimm-fitment]");
    const labels = {};
    finder?.querySelectorAll("select[name]").forEach((select) => {
      labels[select.name] = select.selectedOptions?.[0]?.textContent?.trim() || "";
    });
    const snapshot = event.detail?.vehicle && typeof event.detail.vehicle === "object" ? event.detail.vehicle : {};
    const selection = event.detail?.selection || snapshot.request || {};
    const selectedYear = Number(selection.year || snapshot.request?.year || snapshot.year);
    const selectedMake = String(selection.make || snapshot.request?.make || snapshot.make || "").trim();
    const selectedModel = String(selection.model || snapshot.request?.model || snapshot.model || "").trim();
    if (!selectedYear || !selectedMake || !selectedModel) {
      state.vehicle = null;
      try {
        localStorage.removeItem(KEYS.vehicle);
        localStorage.removeItem(KEYS.localVehicle);
      } catch (_error) { /* Fail closed when a Worker result cannot be normalized. */ }
      document.dispatchEvent(new CustomEvent("dorimm:vehicle-cleared"));
      toast(message("vehicleSelectionCleared", "Vehicle selection cleared"));
      return;
    }
    const normalizedSelection = {
      ...selection,
      year: selectedYear,
      make: selectedMake,
      model: selectedModel,
    };
    state.vehicle = { ...event.detail, selection: normalizedSelection, labels, savedAt: Date.now() };
    write(KEYS.vehicle, state.vehicle);
    const catalogVehicles = window.DORIMM_FITMENT_DATA?.vehicles || [];
    const suppliedId = event.detail?.vehicleId || event.detail?.id || event.detail?.vehicle?.id;
    const catalogVehicle = catalogVehicles.find((vehicle) => vehicle.id === suppliedId) || catalogVehicles.find((vehicle) =>
      selectedYear >= vehicle.yearFrom && selectedYear <= vehicle.yearTo &&
      vehicle.make.toLowerCase() === selectedMake.toLowerCase() &&
      vehicle.model.toLowerCase() === selectedModel.toLowerCase() &&
      (!normalizedSelection.modification || vehicle.trim.toLowerCase().includes(String(normalizedSelection.modification).toLowerCase())),
    );
    if (catalogVehicle) {
      write(KEYS.localVehicle, { id: catalogVehicle.id, year: selectedYear || catalogVehicle.yearFrom, savedAt: Date.now() });
    } else {
      try { localStorage.removeItem(KEYS.localVehicle); }
      catch (_error) { /* The event detail remains the authoritative in-memory vehicle context. */ }
    }
    renderGarage(); applyVehicleMatches();
    document.querySelectorAll("[data-product-fitment-root]").forEach(syncPdpGarage);
    toast(`${vehicleLabel(state.vehicle)} saved to My Garage`);
  });
  document.addEventListener("dorimm:vehicle-cleared", () => {
    state.vehicle = null;
    try {
      localStorage.removeItem(KEYS.vehicle);
      localStorage.removeItem(KEYS.localVehicle);
      const url = new URL(window.location.href);
      url.searchParams.delete("vehicle_id");
      url.searchParams.delete("vehicle_year");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    } catch (_error) { /* The UI still resets when storage or history is unavailable. */ }
    renderGarage();
    applyVehicleMatches();
    document.querySelectorAll("[data-product-fitment-root]").forEach(syncPdpGarage);
  });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init(), { once: true });
  else init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("dorimm:recommendations:loaded", (event) => init(event.detail?.root || document));
  window.DorimmCommerce = { state, init, openCompare };
})();
